import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { OrgData, OrgGroup, OrgMember } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import Avatar from "@/components/base/Avatar";
import OrgName from "@/pages/org/components/OrgName";

// 위원장(role === "위원장")을 찾고, 없으면 members[0] 을 대신 보여준다.
function findChair(group: OrgGroup): OrgMember | undefined {
  const members = group.members ?? [];
  return members.find((m) => m.role === "위원장") ?? members[0];
}

// 메인 홈 «클럽 조직도» 컴팩트 섹션 — 클럽소개 섹션 바로 아래에 위치한다.
// groups 가 비어 있으면 섹션 자체를 렌더링하지 않는다.
export default function OrgSection() {
  const { data, loading } = useApi<OrgData>("org.php");

  const head = data?.head ?? [];
  const exec = data?.exec ?? [];
  const comm = data?.comm ?? [];
  const groups = data?.groups ?? [];

  if (loading) {
    return (
      <section className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-16">
        <div className="mx-auto max-w-6xl space-y-4">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-28 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  // groups 가 비어 있으면 아무것도 렌더링하지 않는다.
  if (groups.length === 0) return null;

  const headGroup = head[0];
  const president = headGroup?.members?.[0];

  return (
    <section className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            클럽 조직도
          </h2>
          <p className="mt-2 text-foreground-600">
            {data?.year ? `${data.year} ` : ""}임원과 위원회 구성
          </p>
        </div>

        <div className="rounded-xl border border-background-200 bg-background-100 p-5 md:p-8">
          {/* 회장 */}
          {headGroup && president && (
            <div className="flex flex-col items-center text-center">
              <Avatar
                name={president.name}
                photo={president.photo}
                color={headGroup.color}
                size={56}
              />
              <OrgName
                member={president}
                className="mt-3 font-heading text-lg font-bold text-foreground-950"
              />
              {headGroup.name && (
                <span className="mt-1 text-sm text-foreground-500">
                  {headGroup.name}
                </span>
              )}
            </div>
          )}

          {/* 임원 */}
          {exec.length > 0 && (
            <div className="mt-8">
              <h3 className="text-center text-xs font-bold uppercase tracking-wider text-foreground-400">
                임원
              </h3>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {exec.map((g, i) => (
                  <div
                    key={g.no ?? i}
                    className="rounded-lg border border-background-200 bg-background-50 px-3 py-3 text-center"
                  >
                    <div className="text-xs font-medium text-foreground-400">
                      {g.name}
                    </div>
                    <div className="mt-1.5 space-y-1">
                      {g.members?.map((m, mi) => (
                        <OrgName
                          key={m.no ?? mi}
                          member={m}
                          className="block text-sm font-bold text-foreground-900"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 위원회 */}
          {comm.length > 0 && (
            <div className="mt-8">
              <h3 className="text-center text-xs font-bold uppercase tracking-wider text-foreground-400">
                위원회
              </h3>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {comm.map((g, i) => {
                  const chair = findChair(g);
                  const total = g.members?.length ?? 0;
                  const others = chair ? total - 1 : 0;
                  return (
                    <div
                      key={g.no ?? i}
                      className="rounded-lg border border-background-200 bg-background-50 px-3 py-3 text-center"
                    >
                      <div className="text-sm font-bold text-foreground-900">
                        {g.name}
                      </div>
                      {chair && (
                        <div className="mt-1.5 text-xs text-foreground-600">
                          {chair.role ? (
                            <span className="font-semibold text-foreground-500">
                              {chair.role}{" "}
                            </span>
                          ) : null}
                          <OrgName member={chair} className="font-medium" />
                        </div>
                      )}
                      {others > 0 && (
                        <div className="mt-1 text-xs text-foreground-400">
                          외 {others}명
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 text-center">
            <Link
              to="/org"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              전체 조직도 보기
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}